import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { login } from '@/api/auth';
import { useBranding } from '@/hooks/useBranding';
import { clearLogoutReason, readLogoutReason, type LogoutReason } from '@/lib/logout-reason';

function describeLogout(r: LogoutReason): string {
  if (r.kind === 'storage') {
    return "The saved session disappeared from this browser's storage (cleared by another tab, an extension or privacy settings). The server did not sign you out.";
  }
  const request = [r.method, r.path].filter(Boolean).join(' ');
  return `The server answered ${r.status ?? 401}${r.message ? ` "${r.message}"` : ''} to ${request || 'a request'}.`;
}

export function Login() {
  const { name: brand } = useBranding();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastLogout] = useState(readLogoutReason);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await login(email, password);

      setAuth(response.token, {
        id: response.admin.id,
        email: response.admin.email,
        name: response.admin.name ?? response.admin.email,
        role: response.admin.role ?? 'user',
        permissions: response.admin.permissions ?? [],
        read_only: !!response.admin.read_only,
        is_active: response.admin.is_active !== false,
      });
      clearLogoutReason();

      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-sunken py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="bg-surface shadow-lg rounded-lg p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-primary">{brand}</h1>
            <p className="mt-2 text-sm text-content-subtle">Sign in to your account</p>
          </div>

          {/* Why the last session ended — a manual sign-out needs no notice */}
          {lastLogout && lastLogout.kind !== 'manual' && (
            <div className="mb-6 p-3 bg-warning-soft border border-warning/30 rounded-md text-sm text-content">
              <p className="font-medium">You were signed out</p>
              <p className="mt-1 text-content-muted">{describeLogout(lastLogout)}</p>
              <p className="mt-1 text-xs text-content-subtle">{new Date(lastLogout.at).toLocaleString()}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-content-muted"
              >
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="admin@example.com"
                disabled={isLoading}
              />
            </div>

            {/* Password field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-content-muted"
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Enter your password"
                disabled={isLoading}
              />
            </div>

            {/* Submit button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer text */}
        <p className="mt-4 text-center text-xs text-content-subtle">
          {brand} Dashboard
        </p>
      </div>
    </div>
  );
}
